import { useCallback, useEffect, useRef, useState } from 'react';
import { getNonCouponItems } from '@hooks/useShoppingCart/coupon';
import { getSessionStatus } from '@services/sessionService';
import { getShoppingCart } from '@services/shoppingCartService';
import { ROUTES } from '@constants/routes';

export interface ResumeState {
  /** Whether an in-progress order with cart items was detected */
  needsResume: boolean;
  /** Target route for resuming: ROUTES.RESIDENTIAL or ROUTES.CHECKOUT */
  resumeRoute: string | null;
  /** Whether the detection check is still in flight */
  isChecking: boolean;
  /** Whether an active work order (SUBMITTED/PROVISIONED) blocks new orders */
  hasActiveWorkOrder: boolean;
  /** Error from the resume detection check, if any */
  error: Error | null;
  /** Whether the resume detection check failed */
  isError: boolean;
}

export interface UseResumeDetectionResult extends ResumeState {
  /** Imperatively trigger a resume check (e.g. after SQ succeeds). */
  checkForResume: () => Promise<boolean>;
  /** Reset state so the modal can be dismissed without re-triggering. */
  reset: () => void;
}

/**
 * Detects whether the given session has an in-progress order that can be resumed.
 *
 * Uses `GET /sessions/{sessionId}` to read `currentStep` + `status`, then for
 * the SELECT step also verifies the cart is non-empty (per AC: empty cart = new order).
 *
 * Used by EntryRouter (app-load) and CheckAvailabilityDialog (post-SQ).
 */
export const useResumeDetection = (sessionId: string | null): UseResumeDetectionResult => {
  const [state, setState] = useState<ResumeState>({
    needsResume: false,
    resumeRoute: null,
    isChecking: false,
    hasActiveWorkOrder: false,
    error: null,
    isError: false,
  });

  const didAutoCheck = useRef(false);

  const ACTIVE_WORK_ORDER_STATUSES = new Set(['SUBMITTED', 'PROVISIONED']);

  const detectResume = useCallback(async (sid: string): Promise<ResumeState> => {
    const noResume: ResumeState = {
      needsResume: false,
      resumeRoute: null,
      isChecking: false,
      hasActiveWorkOrder: false,
      error: null,
      isError: false,
    };

    try {
      const { status, currentStep } = await getSessionStatus(sid);

      if (ACTIVE_WORK_ORDER_STATUSES.has(status)) {
        return { ...noResume, hasActiveWorkOrder: true };
      }

      if (status !== 'IN_PROGRESS') {
        return noResume;
      }

      if (currentStep === 'CHECKOUT') {
        return { ...noResume, needsResume: true, resumeRoute: ROUTES.CHECKOUT };
      }

      if (currentStep === 'SELECT') {
        const cart = await getShoppingCart(sid);
        // A coupon-only cart is not resumable.
        const hasItems = getNonCouponItems(cart).length > 0;
        return {
          ...noResume,
          needsResume: hasItems,
          resumeRoute: hasItems ? ROUTES.RESIDENTIAL : null,
        };
      }

      return noResume;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Resume detection failed');
      return { ...noResume, error, isError: true };
    }
  }, []);

  // Auto-check on mount when sessionId is provided
  useEffect(() => {
    if (!sessionId || didAutoCheck.current) return;
    didAutoCheck.current = true;

    setState((prev) => ({ ...prev, isChecking: true }));
    detectResume(sessionId).then(setState);
  }, [sessionId, detectResume]);

  const checkForResume = useCallback(async (): Promise<boolean> => {
    if (!sessionId) return false;

    setState((prev) => ({ ...prev, isChecking: true }));
    const result = await detectResume(sessionId);
    setState(result);
    return result.needsResume;
  }, [sessionId, detectResume]);

  const reset = useCallback(() => {
    setState({
      needsResume: false,
      resumeRoute: null,
      isChecking: false,
      hasActiveWorkOrder: false,
      error: null,
      isError: false,
    });
  }, []);

  return { ...state, checkForResume, reset };
};
